import React, { useState, useEffect, useRef } from 'react';
import { buildApiUrl } from '../config';
import './DailySales.css';

const DailySales = () => {
    const [dailySales, setDailySales] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [userRole, setUserRole] = useState('');
    const [editingRecord, setEditingRecord] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({});
    const [selectedRow, setSelectedRow] = useState(-1);
    const containerRef = useRef(null);

    useEffect(() => {
        // Get user role from session storage
        const userData = JSON.parse(sessionStorage.getItem('user'));
        console.log('User data from session storage:', userData);
        if (userData && userData.role) {
            setUserRole(userData.role);
            console.log('User role set to:', userData.role);
        } else {
            console.log('No user role found in session storage');
        }
    }, []);

    useEffect(() => {
        if (selectedDate) {
            fetchDailySales();
        }
    }, [selectedDate]);

    useEffect(() => {
        if (selectedRow < 0 || !containerRef.current) return;

        const rows = containerRef.current.querySelectorAll('tbody tr:not(.total-row)');
        if (rows[selectedRow]) {
            rows[selectedRow].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [selectedRow]);

    const handleKeyDown = (e) => {
        if (!dailySales.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedRow((prev) => Math.min(prev + 1, dailySales.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedRow((prev) => Math.max(prev - 1, 0));
        }
    };

    const formatAmount = (value) => {
        if (value === null || value === undefined || value === '' || value === 0 || value === '0') {
            return '-';
        }
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return Number.isInteger(num) ? String(num) : num.toFixed(2);
    };

    // UI-only: merge Oil Change / Washing / Other Cash Sales into one Cash Sales column.
    // Credit and POS amounts must not appear here.
    const getCashSalesAmount = (sale) => {
        if (parseFloat(sale.CreditSales)) return null;
        if (parseFloat(sale.PosSales)) return null;
        const cash = (parseFloat(sale.OilChange) || 0)
            + (parseFloat(sale.Washing) || 0)
            + (parseFloat(sale.OtherCashSales) || 0);
        return cash || null;
    };

    const getCreditSalesAmount = (sale) => {
        const credit = parseFloat(sale.CreditSales);
        return credit || null;
    };

    const getPosSalesAmount = (sale) => {
        const pos = parseFloat(sale.PosSales);
        return pos || null;
    };

    const fetchDailySales = async () => {
        try {
            const response = await fetch(buildApiUrl(`api/dailySales?date=${selectedDate}`));
            const data = await response.json();
            if (data.success) {
                console.log('Daily Sales Data:', data.data); // Debug log
                setDailySales(data.data);
                setSelectedRow(-1);
            } else {
                console.error('Error fetching daily sales data:', data.error);
            }
        } catch (err) {
            console.error('Error fetching daily sales data:', err);
        }
    };

    // Display-only: formats dates as DD-Mmm-YYYY (e.g. 26-Jun-2026). Does not affect API or date picker.
    const formatDate = (dateString) => {
        if (!dateString || String(dateString).toLowerCase() === 'null') return '-';

        const monthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const s = String(dateString).trim();
        let day;
        let month;
        let year;

        let match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            year = +match[1];
            month = +match[2];
            day = +match[3];
        } else if ((match = s.match(/^(\d{2})-(\d{2})-(\d{4})$/))) {
            day = +match[1];
            month = +match[2];
            year = +match[3];
        } else if ((match = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/))) {
            day = +match[1];
            month = +match[2];
            year = +match[3];
        } else if ((match = s.match(/^(\d{1,2})-([a-zA-Z]{3})-(\d{4})$/))) {
            const monthIndex = monthAbbr.indexOf(match[2].toLowerCase());
            if (monthIndex >= 0) {
                return `${String(match[1]).padStart(2, '0')}-${monthAbbr[monthIndex]}-${match[3]}`;
            }
            return s;
        } else {
            return 'Invalid Date';
        }

        if (month < 1 || month > 12 || day < 1 || day > 31) return 'Invalid Date';
        return `${String(day).padStart(2, '0')}-${monthAbbr[month - 1]}-${year}`;
    };


    const totals = dailySales.reduce(
        (acc, sale) => {
            acc.OilChange += parseFloat(sale.OilChange) || 0;
            acc.Washing += parseFloat(sale.Washing) || 0;
            acc.OtherCashSales += parseFloat(sale.OtherCashSales) || 0;
            acc.Recovery += parseFloat(sale.Recovery) || 0;
            acc.CreditSales += parseFloat(sale.CreditSales) || 0;
            acc.PosSales += parseFloat(sale.PosSales) || 0;
            acc.Commission -= parseFloat(sale.Commission) || 0;
            const rowCashSales = getCashSalesAmount(sale);
            if (rowCashSales) acc.CashSales += rowCashSales;
            // Include regular expenses and settlements, but exclude overtime payments from expense calculation
            // Overtime payments are identified by having OvertimeID AND Service starting with "Overtime:"
            if (sale.Expense && !(sale.OvertimeID && sale.Service?.startsWith('Overtime:'))) {
                acc.Expense += parseFloat(sale.Expense) || 0;
            }
            acc.Balance = sale.Balance || acc.Balance;
            return acc;
        },
        { OilChange: 0, Washing: 0, OtherCashSales: 0, CashSales: 0, Recovery: 0, CreditSales: 0, PosSales: 0, Commission: 0, Expense: 0, Balance: 0 }
    );

    // Cash received (cash in hand): cash sales + recovery only.
    // POS and credit are excluded so they are not treated as cash in hand.
    totals.cashReceived = totals.CashSales + totals.Recovery;
    
    // Calculate cash receivables: cash received - expenses - commission
    totals.cashReceivables = totals.cashReceived - totals.Expense - Math.abs(totals.Commission);

    // Total sale amount for the day (cash + credit + POS)
    totals.totalSaleAmount = totals.CashSales + totals.CreditSales + totals.PosSales;


    // Export currently displayed Daily Sales to CSV
    const exportDailySalesCSV = () => {
        if (!dailySales || dailySales.length === 0) {
            alert('No data to export');
            return;
        }

        const headers = [
            'Sr',
            'Date',
            'Service',
            'Vehicle Number',
            'Vehicle Type',
            'Driver',
            'Company Name',
            'Cash Sales',
            'Recovery',
            'Credit Sales',
            'POS',
            'Commission',
            'Expense',
            // ID columns commented out - can be uncommented if needed later
            // 'SaleID',
            // 'ExpenseID',
            // 'PaymentID',
            // 'OvertimeID',
            // 'SettlementID',
            'OT',
            'Remarks',
            'Balance'
        ];

        const rows = dailySales.map((sale, idx) => [
            String(idx + 1),
            sale.Date ? formatDate(sale.Date) : '-',
            sale.Service ?? '-',
            sale.VehicleNumber ?? '-',
            sale.VehicleType ?? '-',
            sale.Driver ?? '-',
            sale.CompanyName ?? '-',
            getCashSalesAmount(sale) ?? '-',
            sale.Recovery ?? '-',
            getCreditSalesAmount(sale) ?? '-',
            getPosSalesAmount(sale) ?? '-',
            sale.Commission ?? '-',
            sale.Expense ?? '-',
            // ID columns commented out - can be uncommented if needed later
            // sale.SaleID ?? '-',
            // sale.ExpenseID ?? '-',
            // sale.PaymentID ?? '-',
            // sale.OvertimeID ?? '-',
            // sale.SettlementID ?? '-',
            sale.OvertimeAmount ?? '-',
            sale.Remarks ?? '-',
            sale.Balance ?? '-',
        ]);

        // Add totals row at the end
        rows.push([
            'Total', '', '', '', '', '', '',
            totals.CashSales.toFixed(2),
            totals.Recovery.toFixed(2),
            totals.CreditSales.toFixed(2),
            totals.PosSales.toFixed(2),
            Math.abs(totals.Commission).toFixed(2),
            totals.Expense.toFixed(2),
            '', '', // OT and Remarks
            String(totals.Balance),
        ]);

        const csv = [headers, ...rows]
            .map((row) => row
                .map((cell) => {
                    const val = cell === null || cell === undefined ? '' : String(cell);
                    // Escape quotes and wrap in quotes if contains comma or newline
                    const escaped = val.replace(/"/g, '""');
                    return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
                })
                .join(','))
            .join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileDate = selectedDate || new Date().toISOString().split('T')[0];
        link.download = `Daily_Sales_${fileDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleEdit = (sale) => {
        setEditingRecord(sale);
        setEditFormData({
            // Common fields
            servicePrice: sale.OilChange || sale.Washing || sale.CreditSales || sale.Expense || sale.Recovery,
            notes: sale.Remarks,
            // Sales specific
            paymentMethod: sale.payment_method || (sale.CreditSales ? 'credit' : 'cash'), // Set default based on CreditSales
            commission: sale.Commission,
            // Expense specific
            expenseAmount: sale.Expense,
            expenseType: sale.Service?.replace('Expense: ', ''),
            expenseDescription: sale.Remarks,
            // Payment specific
            paymentAmount: sale.Recovery,
            paymentNotes: sale.Remarks
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async () => {
        if (!window.confirm('Are you sure you want to update this record?')) {
            return;
        }

        try {
            const response = await fetch(buildApiUrl('api/dailySales/edit'), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    saleId: editingRecord.SaleID,
                    expenseId: editingRecord.ExpenseID,
                    paymentId: editingRecord.PaymentID,
                    ...editFormData
                })
            });

            const data = await response.json();

            if (data.success) {
                // Refresh the data
                fetchDailySales();
                setShowEditModal(false);
                alert('Record updated successfully');
            } else {
                alert('Failed to update record: ' + data.error);
            }
        } catch (error) {
            console.error('Error updating record:', error);
            alert('Error updating record. Please try again.');
        }
    };

    const renderEditForm = () => {
        if (!editingRecord) return null;

        return (
            <div className="edit-modal">
                <div className="edit-modal-content">
                    <h2>Edit Record</h2>
                    {editingRecord.SaleID && (
                        <>
                            <div className="form-group">
                                <label>Service Price:</label>
                                <input
                                    type="number"
                                    value={editFormData.servicePrice || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, servicePrice: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Payment Method:</label>
                                <input
                                    type="text"
                                    value={editFormData.paymentMethod || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, paymentMethod: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Commission:</label>
                                <input
                                    type="number"
                                    value={editFormData.commission || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, commission: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    {editingRecord.ExpenseID && (
                        <>
                            <div className="form-group">
                                <label>Expense Amount:</label>
                                <input
                                    type="number"
                                    value={editFormData.expenseAmount || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, expenseAmount: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Expense Type:</label>
                                <input
                                    type="text"
                                    value={editFormData.expenseType || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, expenseType: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    {editingRecord.PaymentID && (
                        <>
                            <div className="form-group">
                                <label>Payment Amount:</label>
                                <input
                                    type="number"
                                    value={editFormData.paymentAmount || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, paymentAmount: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    <div className="form-group">
                        <label>Notes:</label>
                        <input
                            type="text"
                            value={editFormData.notes || ''}
                            onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                        />
                    </div>
                    <div className="modal-buttons">
                        <button type="button" className="save-btn" onClick={handleEditSubmit}>Save Changes</button>
                        <button type="button" className="cancel-btn" onClick={() => setShowEditModal(false)}>Cancel</button>
                    </div>
                </div>
            </div>
        );
    };

    const handleDelete = async (sale) => {
        // Show confirmation dialog
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        try {
            const response = await fetch(buildApiUrl('api/dailySales/delete'), {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    saleId: sale.SaleID,
                    expenseId: sale.ExpenseID,
                    paymentId: sale.PaymentID,
                    overtimeId: sale.OvertimeID,
                    settlementId: sale.SettlementID,
                    stockTransactionId: sale.StockTransactionID
                })
            });

            const data = await response.json();

            if (data.success) {
                // Remove the deleted record from the state
                setDailySales(prevSales => prevSales.filter(item => {
                    if (sale.SaleID) return item.SaleID !== sale.SaleID;
                    if (sale.ExpenseID) return item.ExpenseID !== sale.ExpenseID;
                    if (sale.PaymentID) return item.PaymentID !== sale.PaymentID;
                    if (sale.OvertimeID) return item.OvertimeID !== sale.OvertimeID;
                    if (sale.SettlementID) return item.SettlementID !== sale.SettlementID;
                    if (sale.StockTransactionID) return item.StockTransactionID !== sale.StockTransactionID;
                    return true;
                }));

                alert('Record deleted successfully');
            } else {
                alert('Failed to delete record: ' + data.error);
            }
        } catch (error) {
            console.error('Error deleting record:', error);
            alert('Error deleting record. Please try again.');
        }
    };

    return (
        <div className="daily-sales-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                <h1 style={{ margin: 0 }}>Daily Sales</h1>
                <button
                    onClick={exportDailySalesCSV}
                    style={{
                        background: 'none',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#217346',
                        padding: '6px 10px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                    title="Export current table to CSV"
                >
                    📊
                </button>
            </div>
            <div className="filter-container">
                <label htmlFor="date-filter">Select Date:</label>
                <input
                    type="date"
                    id="date-filter"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                />
            </div>
            <div
                className="table-container"
                tabIndex={0}
                ref={containerRef}
                onKeyDown={handleKeyDown}
            >
                <table className="daily-sales-table">
                    <thead>
                        <tr>
                            <th>Sr</th>
                            <th>Date</th>
                            <th>Service</th>
                            <th>Vehicle Number</th>
                            <th>Vehicle Type</th>
                            {/* <th>Driver</th> */}
                            <th className="company-name-header">Company Name</th>
                            <th>Cash Sales</th>
                            <th>Recovery</th>
                            <th>Credit Sales</th>
                            <th>POS</th>
                            <th>Commission</th>
                            <th>Expense</th>
                            {/* ID columns commented out - can be uncommented if needed later
                            <th>SaleID</th>
                            <th>ExpenseID</th>
                            <th>PaymentID</th>
                            <th>OvertimeID</th>
                            <th>SettlementID</th>
                            */}
                            <th>OT</th>
                            <th>Remarks</th>
                            <th>Balance</th>
                            {userRole === 'manager' && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {dailySales.length > 0 ? (
                            dailySales.map((sale, index) => (
                                sale.Service !== null ? (
                                    <tr
                                        key={index}
                                        onClick={() => setSelectedRow(index)}
                                        className={index === selectedRow ? 'highlighted-row' : ''}
                                    >
                                        <td>{index + 1}</td>
                                        <td>{formatDate(sale.Date)}</td>
                                        <td>{sale.Service}</td>
                                        <td>{sale.VehicleNumber}</td>
                                        <td>{sale.VehicleType}</td>
                                        {/* <td>{sale.Driver}</td> */}
                                        <td className="company-name-cell">{sale.CompanyName}</td>
                                        <td>{formatAmount(getCashSalesAmount(sale))}</td>
                                        <td>{formatAmount(sale.Recovery)}</td>
                                        <td>{formatAmount(getCreditSalesAmount(sale))}</td>
                                        <td>{formatAmount(getPosSalesAmount(sale))}</td>
                                        <td>{formatAmount(sale.Commission)}</td>
                                        <td>{formatAmount(sale.Expense)}</td>
                                        {/* ID columns commented out - can be uncommented if needed later
                                        <td>{sale.SaleID || '-'}</td>
                                        <td>{sale.ExpenseID || '-'}</td>
                                        <td>{sale.PaymentID || '-'}</td>
                                        <td>{sale.OvertimeID || '-'}</td>
                                        <td>{sale.SettlementID || '-'}</td>
                                        */}
                                        <td>{formatAmount(sale.OvertimeAmount)}</td>
                                        <td>{sale.Remarks}</td>
                                        <td>{formatAmount(sale.Balance)}</td>
                                        {userRole === 'manager' && (
                                            <td className="actions-cell">
                                                <button
                                                    className="edit-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(sale);
                                                    }}
                                                    title="Edit"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    className="delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(sale);
                                                    }}
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ) : (
                                    // Separate row for expenses
                                    <tr
                                        key={`expense-${index}`}
                                        className={`expense-row${index === selectedRow ? ' highlighted-row' : ''}`}
                                        onClick={() => setSelectedRow(index)}
                                    >
                                        <td colSpan="6"><strong>Expense</strong></td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td><strong>{formatAmount(sale.Expense)}</strong></td>
                                        <td>-</td>
                                        <td>{sale.Remarks || '-'}</td>
                                        <td>-</td>
                                        {userRole === 'manager' && <td></td>}
                                    </tr>
                                )
                            ))
                        ) : (
                            <tr>
                                <td colSpan={userRole === 'manager' ? 16 : 15}>No data available for the selected date.</td>
                            </tr>
                        )}
                        {/* Add total row */}
                        {dailySales.length > 0 && (
                            <tr className="total-row">
                                <td colSpan="6"><strong>Total</strong></td>
                                <td><strong>{totals.CashSales > 0 ? totals.CashSales.toFixed(2) : '-'}</strong></td>
                                <td><strong>{totals.Recovery > 0 ? totals.Recovery.toFixed(2) : '-'}</strong></td>
                                <td><strong>{totals.CreditSales > 0 ? totals.CreditSales.toFixed(2) : '-'}</strong></td>
                                <td><strong>{totals.PosSales > 0 ? totals.PosSales.toFixed(2) : '-'}</strong></td>
                                <td><strong>{totals.Commission !== 0 ? Math.abs(totals.Commission).toFixed(2) : '-'}</strong></td>
                                <td><strong>{totals.Expense > 0 ? totals.Expense.toFixed(2) : '-'}</strong></td>
                                <td>-</td>
                                <td>-</td>
                                <td><strong>{totals.Balance !== 0 ? totals.Balance : '-'}</strong></td>
                                {userRole === 'manager' && <td>-</td>}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ✅ Summary Card Goes Here */}
            <div className="summary-card">
                <h2>Summary</h2>
                <div className="summary-content">
                    <div className="summary-row">
                        <span>Date:</span>
                        <span>{selectedDate ? formatDate(selectedDate) : '-'}</span>
                    </div>
                    <div className="summary-row highlighted">
                        <span>Cash Sales:</span>
                        <span>{totals.CashSales > 0 ? totals.CashSales.toFixed(2) : '0.00'}</span>
                    </div>
                    <div className="summary-row">
                        <span>Credit Sales:</span>
                        <span>{totals.CreditSales}</span>
                    </div>
                    <div className="summary-row">
                        <span>POS:</span>
                        <span>{totals.PosSales > 0 ? totals.PosSales.toFixed(2) : '0.00'}</span>
                    </div>
                    <hr />
                    <div className="summary-row total">
                        <span>Total Sale Amount:</span>
                        <span>{totals.totalSaleAmount}</span>
                    </div>
                    <div className="summary-row expense">
                        <span>Less Expenditures:</span>
                        <span>{totals.Expense}</span>
                    </div>
                    <div className="summary-row expense">
                        <span>Less Commission:</span>
                        <span>{totals.Commission}</span>
                    </div>
                    <div className="summary-row expense">
                        <span>Recovery:</span>
                        <span>{totals.Recovery}</span>
                    </div>
                    <hr />
                    <div className="summary-row total-final">
                        <span>Balance:</span>
                        <span>{totals.Balance}</span>
                    </div>
                    {/* Cash Received row commented out - Cash Receivables now acts as Cash Received
                    <div className="summary-row cash-received">
                        <span>Cash Received:</span>
                        <span>{totals.cashReceived > 0 ? `AED ${totals.cashReceived.toFixed(2)}` : 'AED 0.00'}</span>
                    </div>
                    */}
                    <div className="summary-row cash-receivables">
                        <span><strong>Cash Received:</strong></span>
                        <span><strong>{totals.cashReceivables !== undefined ? `AED ${totals.cashReceivables.toFixed(2)}` : 'AED 0.00'}</strong></span>
                    </div>
                </div>
            </div>
            {showEditModal && renderEditForm()}
        </div>
    );

};

export default DailySales;
